import { useCallback, useState } from 'react'

import { downloadFile } from '@modules/utils/download'

interface UseImageDownloadOptions {
  /** ダウンロードする画像のURL */
  url?: string
  /** ファイル名のプレフィックス（例: "案件名"）。拡張子は自動付与される */
  filenamePrefix?: string
  /** エラー発生時のコールバック */
  onError?: (error: Error) => void
}

interface UseImageDownloadReturn {
  /** ダウンロードを実行する関数 */
  download: () => Promise<void>
  /** ダウンロード中かどうか */
  isDownloading: boolean
  /** エラー情報 */
  error: Error | null
}

/**
 * 画像ダウンロード処理をカプセル化するフック
 * 拡張子はblob.typeから自動取得し、タイムスタンプ付きファイル名で保存
 */
export const useImageDownload = ({
  url,
  filenamePrefix = '生成画像',
  onError,
}: UseImageDownloadOptions): UseImageDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const download = useCallback(async () => {
    if (!url) {
      return
    }

    setIsDownloading(true)
    setError(null)

    try {
      // タイムスタンプ生成: 2023-10-27_10-30-00 形式
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-')
      // 拡張子なしのファイル名を渡し、autoExtensionで自動付与
      const filename = `${filenamePrefix}_${timestamp}`

      await downloadFile(url, filename, { autoExtension: true })
    } catch (e) {
      const err = e instanceof Error ? e : new Error('ダウンロードに失敗しました')
      setError(err)
      console.error('画像のダウンロードに失敗しました', e)
      onError?.(err)
    } finally {
      setIsDownloading(false)
    }
  }, [url, filenamePrefix, onError])

  return { download, isDownloading, error }
}
